import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

interface TextOverlayProps {
  text: string;
  position?: 'top' | 'center' | 'bottom';
  fontSize?: number;
  fontFamily?: string;
  color?: string;
  backgroundColor?: string;
  animation?: 'none' | 'fade-in' | 'slide-up' | 'scale';
  delay?: number; // Delay in frames before animation starts
}

export const TextOverlay: React.FC<TextOverlayProps> = ({
  text,
  position = 'top',
  fontSize = 36,
  fontFamily = 'Inter',
  color = '#ffffff',
  backgroundColor,
  animation = 'fade-in',
  delay = 0,
}) => {
  const frame = useCurrentFrame();
  const { fps, height } = useVideoConfig();

  if (!text) {
    return null;
  }

  const adjustedFrame = Math.max(0, frame - delay);

  // Animation calculations
  let opacity = 1;
  let translateY = 0;
  let scale = 1;

  if (animation === 'fade-in') {
    opacity = interpolate(adjustedFrame, [0, 15], [0, 1], {
      extrapolateRight: 'clamp',
    });
  }

  if (animation === 'slide-up') {
    opacity = interpolate(adjustedFrame, [0, 10], [0, 1], {
      extrapolateRight: 'clamp',
    });
    translateY = interpolate(adjustedFrame, [0, 15], [30, 0], {
      extrapolateRight: 'clamp',
    });
  }

  if (animation === 'scale') {
    const springValue = spring({
      frame: adjustedFrame,
      fps,
      config: {
        damping: 12,
        stiffness: 200,
        mass: 0.5,
      },
    });
    scale = interpolate(springValue, [0, 1], [0.5, 1]);
    opacity = interpolate(adjustedFrame, [0, 5], [0, 1], {
      extrapolateRight: 'clamp',
    });
  }

  // Position styles
  const getPositionStyle = (): React.CSSProperties => {
    const padding = Math.round(height * 0.08);
    switch (position) {
      case 'top':
        return { top: padding, bottom: 'auto' };
      case 'center':
        return { top: '50%', transform: `translateY(-50%) translateY(${translateY}px) scale(${scale})` };
      case 'bottom':
      default:
        return { bottom: padding, top: 'auto' };
    }
  };

  const positionStyle = getPositionStyle();
  const transform = position !== 'center'
    ? `translateY(${translateY}px) scale(${scale})`
    : positionStyle.transform;

  return (
    <AbsoluteFill
      style={{
        ...positionStyle,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0 5%',
        opacity,
        transform: position !== 'center' ? transform : undefined,
      }}
    >
      <div
        style={{
          fontFamily,
          fontSize,
          fontWeight: 700,
          color,
          textAlign: 'center',
          textShadow: '2px 2px 8px rgba(0,0,0,0.6)',
          ...(backgroundColor && {
            backgroundColor,
            padding: '12px 24px',
            borderRadius: 8,
          }),
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
